// src/utils/sessionClock.js

const CLOCK_KEY = "flpa_session_clock";

/**
 * Cada pestaña tiene su propio performance.now(),
 * pero solamente la pestaña dueña va a descontar tiempo.
 *
 * El valor compartido entre pestañas es remainingMs.
 * Nunca usamos Date.now() para calcular vencimiento.
 */

let ownerAnchorPerfMs = null;
let ownerAnchorRemainingMs = null;

/**
 * Extrae la duración total del JWT usando únicamente
 * valores generados por el servidor.
 *
 * exp - iat = duración del token
 */
export function getTokenTtlMs(token) {
  if (!token) return 0;

  try {
    const payload = token.split(".")[1];

    const json = atob(
      payload.replace(/-/g, "+").replace(/_/g, "/")
    );

    const claims = JSON.parse(
      decodeURIComponent(escape(json))
    );

    const iat = Number(claims?.iat);
    const exp = Number(claims?.exp);

    if (
      !Number.isFinite(iat) ||
      !Number.isFinite(exp) ||
      exp <= iat
    ) {
      return 0;
    }

    return (exp - iat) * 1000;
  } catch {
    return 0;
  }
}

/**
 * Lee el último valor global publicado.
 */
export function readSessionClock() {
  try {
    const raw = localStorage.getItem(CLOCK_KEY);

    if (!raw) return null;

    const clock = JSON.parse(raw);

    if (!Number.isFinite(Number(clock?.remainingMs))) {
      return null;
    }

    return clock;
  } catch {
    return null;
  }
}

/**
 * Inicializa/reinicia el reloj.
 *
 * Esto se usa:
 * - después del login
 * - después de un keep-alive exitoso
 *
 * ttlMs viene de exp - iat, NO de Date.now().
 */
export function resetSessionClock(ttlMs, ownerTabId) {
  const value = Number(ttlMs);

  if (!Number.isFinite(value) || value <= 0) {
    return false;
  }

  ownerAnchorRemainingMs = value;
  ownerAnchorPerfMs = performance.now();

  publishSessionClock(value, ownerTabId);

  return true;
}

/**
 * Una pestaña que pasa a ser dueña continúa desde
 * el último tiempo global disponible.
 *
 * NO vuelve a arrancar desde el TTL completo.
 */
export function adoptSessionClock(remainingMs) {
  const value = Number(remainingMs);

  if (!Number.isFinite(value) || value <= 0) {
    ownerAnchorRemainingMs = 0;
    ownerAnchorPerfMs = performance.now();
    return false;
  }

  ownerAnchorRemainingMs = value;
  ownerAnchorPerfMs = performance.now();

  return true;
}

/**
 * Calcula cuánto queda utilizando solamente
 * tiempo monotónico del navegador.
 *
 * Solamente debe usarlo la pestaña dueña.
 */
export function calculateOwnerRemainingMs() {
  if (
    ownerAnchorPerfMs == null ||
    ownerAnchorRemainingMs == null
  ) {
    return null;
  }

  const elapsedMs =
    performance.now() - ownerAnchorPerfMs;

  return Math.max(
    0,
    ownerAnchorRemainingMs - elapsedMs
  );
}

/**
 * Publica el tiempo global.
 * Las demás pestañas solamente consumen este valor.
 */
export function publishSessionClock(remainingMs, ownerTabId) {
  const value = Math.max(0, Number(remainingMs) || 0);

  const clock = {
    remainingMs: value,
    ownerTabId: ownerTabId || null,
  };

  localStorage.setItem(
    CLOCK_KEY,
    JSON.stringify(clock)
  );

  return clock;
}

/**
 * Lo usará sessionManager para que la pestaña dueña
 * actualice el contador global.
 */
export function tickSessionClock(ownerTabId) {
  const remainingMs = calculateOwnerRemainingMs();

  if (remainingMs == null) {
    return null;
  }

  publishSessionClock(
    remainingMs,
    ownerTabId
  );

  return remainingMs;
}

/**
 * Para consumidores.
 *
 * Las pestañas que NO son dueñas no hacen ninguna
 * resta: leen el último valor que publicó la dueña.
 */
export function getSharedRemainingMs() {
  const clock = readSessionClock();

  if (!clock) return null;

  return Math.max(
    0,
    Number(clock.remainingMs)
  );
}

export function clearSessionClock() {
  ownerAnchorPerfMs = null;
  ownerAnchorRemainingMs = null;

  localStorage.removeItem(CLOCK_KEY);
}

export { CLOCK_KEY };